<template>
  <NavBar pageName="IMSI Groups"></NavBar>

  <!-- Snackbar -->
  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
    {{ snackbarMessage }}
  </v-snackbar>

  <!-- Create Group Dialog -->
  <v-dialog v-model="showCreateDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-folder-plus</v-icon>
        Create New Group
      </v-card-title>
      <v-card-text>
        <ClientSearchFilter
          v-model="newGroupClientId"
          :show-include-subclients="false"
          client-select-label="Client proprietar *"
          @hierarchy-loaded="onCreateHierarchyLoaded"
        />
        <v-text-field
          v-model="newGroupName"
          label="Group Name"
          required
          density="compact"
          class="mb-3"
        ></v-text-field>
        <v-textarea
          v-model="newGroupDescription"
          label="Description (optional)"
          rows="2"
          density="compact"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showCreateDialog = false">Cancel</v-btn>
        <v-btn color="primary" :loading="createLoading" :disabled="!newGroupName || !newGroupClientId" @click="createGroup">
          Create
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Edit Group Dialog -->
  <v-dialog v-model="showEditDialog" max-width="500">
    <v-card>
      <v-card-title>
        <v-icon class="mr-2">mdi-pencil</v-icon>
        Edit Group
      </v-card-title>
      <v-card-text>
        <ClientSearchFilter
          v-model="editingGroup.client_id"
          :show-include-subclients="false"
          client-select-label="Client proprietar"
        />
        <v-text-field
          v-model="editingGroup.name"
          label="Group Name"
          required
          density="compact"
          class="mb-3"
        ></v-text-field>
        <v-textarea
          v-model="editingGroup.description"
          label="Description"
          rows="2"
          density="compact"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showEditDialog = false">Cancel</v-btn>
        <v-btn color="primary" :loading="editLoading" @click="updateGroup">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Confirmation Dialog -->
  <v-dialog v-model="showDeleteDialog" max-width="400">
    <v-card>
      <v-card-title>Delete Group</v-card-title>
      <v-card-text>
        Are you sure you want to delete the group <strong>"{{ deletingGroup?.name }}"</strong>?
        This will remove the group but not the IMSIs themselves.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
        <v-btn color="error" :loading="deleteLoading" @click="deleteGroup">Delete</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- View Members Dialog -->
  <v-dialog v-model="showMembersDialog" max-width="900">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>
          <v-icon class="mr-2">mdi-account-group</v-icon>
          {{ selectedGroup?.name }} - Members
        </span>
        <v-btn icon size="small" @click="showMembersDialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-data-table
          v-model="selectedMembers"
          :headers="memberHeaders"
          :items="groupMembers"
          :loading="loadingMembers"
          show-select
          item-value="imsi_id"
          density="compact"
        >
          <template v-slot:[`item.imsi`]="{ item }">
            <span class="font-weight-bold">{{ item.imsi || '-' }}</span>
          </template>
          <template v-slot:[`item.sim_status`]="{ item }">
            <v-chip :color="getStatusColor(item.sim_status)" size="x-small">
              {{ item.sim_status || 'N/A' }}
            </v-chip>
          </template>
        </v-data-table>

        <!-- Pagination for members -->
        <v-pagination
          v-if="membersTotalPages > 1"
          v-model="membersPage"
          :length="membersTotalPages"
          @update:model-value="loadGroupMembers"
          class="mt-4"
        ></v-pagination>
      </v-card-text>
      <v-card-actions>
        <v-btn
          v-if="selectedMembers.length > 0"
          color="error"
          variant="text"
          :loading="removeLoading"
          @click="removeSelectedMembers"
        >
          Remove {{ selectedMembers.length }} from group
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn color="red" @click="showMembersDialog = false">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-container style="margin-top: 185px;">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12" class="d-flex justify-space-between align-center">
        <h2>IMSI Groups</h2>
        <v-btn color="primary" @click="openCreateDialog">
          <v-icon class="mr-1">mdi-plus</v-icon>
          Create Group
        </v-btn>
      </v-col>
    </v-row>

    <!-- Groups Table -->
    <v-card>
      <v-data-table
        :headers="tableHeaders"
        :items="groups"
        :loading="loading"
        class="elevation-1"
      >
        <template v-slot:[`item.name`]="{ item }">
          <span class="font-weight-bold clickable" @click="viewMembers(item)">
            {{ item.name }}
          </span>
        </template>

        <template v-slot:[`item.member_count`]="{ item }">
          <v-chip size="small" color="primary">{{ item.member_count }}</v-chip>
        </template>

        <template v-slot:[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-btn variant="text" size="small" color="primary" @click="viewMembers(item)">
            <v-icon size="small">mdi-eye</v-icon>
            View
          </v-btn>
          <v-btn variant="text" size="small" color="warning" @click="openEditDialog(item)">
            <v-icon size="small">mdi-pencil</v-icon>
            Edit
          </v-btn>
          <v-btn variant="text" size="small" color="error" @click="confirmDelete(item)">
            <v-icon size="small">mdi-delete</v-icon>
            Delete
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Empty state -->
    <v-card v-if="!loading && groups.length === 0" class="pa-6 text-center mt-4">
      <v-icon size="64" color="grey">mdi-folder-outline</v-icon>
      <p class="text-h6 mt-4">No IMSI groups yet</p>
      <p class="text-body-2 text-grey">Create a group to organize your IMSIs</p>
      <v-btn color="primary" class="mt-4" @click="openCreateDialog">
        <v-icon class="mr-1">mdi-plus</v-icon>
        Create Group
      </v-btn>
    </v-card>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue'
import useGetLoggedIn from "@/composables/useGetLoggedIn"
import { log } from "@/composables/useLog"
import NavBar from "@/components/NavBar.vue"
import ClientSearchFilter from "@/components/ClientSearchFilter.vue"
import store from "@/store"
import {
  getImsiGroups,
  createImsiGroup,
  updateImsiGroup,
  deleteImsiGroup,
  getImsiGroupMembers,
  removeImsisFromGroup
} from "@/composables/useGroupsAPI"

export default defineComponent({
  name: 'ImsiGroupsList',
  components: { NavBar, ClientSearchFilter },
  data() {
    return {
      loading: false,
      token: '',

      // Groups data
      groups: [],

      // Table headers
      tableHeaders: [
        { title: 'Name', key: 'name', sortable: true },
        { title: 'Description', key: 'description', sortable: false },
        { title: 'Client', key: 'client_name', sortable: true },
        { title: 'Members', key: 'member_count', sortable: true, width: '100px' },
        { title: 'Created', key: 'created_at', sortable: true, width: '120px' },
        { title: 'Actions', key: 'actions', sortable: false, width: '200px' }
      ],

      // Create dialog
      showCreateDialog: false,
      newGroupName: '',
      newGroupDescription: '',
      newGroupClientId: null,
      createLoading: false,

      // Edit dialog
      showEditDialog: false,
      editingGroup: { id: null, name: '', description: '', client_id: null },
      editLoading: false,

      // Delete dialog
      showDeleteDialog: false,
      deletingGroup: null,
      deleteLoading: false,

      // Members dialog
      showMembersDialog: false,
      selectedGroup: null,
      groupMembers: [],
      loadingMembers: false,
      membersPage: 1,
      membersTotalPages: 1,
      memberHeaders: [
        { title: 'Serial', key: 'serial', width: '120px' },
        { title: 'IMSI', key: 'imsi', width: '150px' },
        { title: 'MSISDN', key: 'msisdn', width: '120px' },
        { title: 'Status', key: 'sim_status', width: '100px' },
        { title: 'Client', key: 'client_name' }
      ],
      selectedMembers: [],
      removeLoading: false,

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    }
  },
  methods: {
    async loadGroups() {
      this.loading = true
      try {
        const response = await getImsiGroups(this.token)
        if (!response.error) {
          this.groups = response.groups || []
        } else {
          this.showSnackbarMessage(response.message || 'Error loading groups', 'error')
        }
      } catch (err) {
        log("Error loading groups:", err)
        this.showSnackbarMessage('Error loading groups', 'error')
      } finally {
        this.loading = false
      }
    },

    onCreateHierarchyLoaded({ currentClientId }) {
      if (!this.newGroupClientId && currentClientId) {
        this.newGroupClientId = currentClientId
      }
    },

    openCreateDialog() {
      this.newGroupName = ''
      this.newGroupDescription = ''
      this.newGroupClientId = null
      this.showCreateDialog = true
    },

    async createGroup() {
      if (!this.newGroupName) return
      this.createLoading = true
      try {
        const response = await createImsiGroup(this.token, this.newGroupName, this.newGroupDescription, [], this.newGroupClientId)
        if (!response.error) {
          this.showSnackbarMessage('Group created successfully', 'success')
          this.showCreateDialog = false
          this.loadGroups()
        } else {
          this.showSnackbarMessage(response.message || 'Error creating group', 'error')
        }
      } catch (err) {
        log("Error creating group:", err)
        this.showSnackbarMessage('Error creating group', 'error')
      } finally {
        this.createLoading = false
      }
    },

    openEditDialog(group) {
      this.editingGroup = { id: group.id, name: group.name, description: group.description || '', client_id: group.client_id || null }
      this.showEditDialog = true
    },

    async updateGroup() {
      this.editLoading = true
      try {
        const response = await updateImsiGroup(this.token, this.editingGroup.id, this.editingGroup.name, this.editingGroup.description, this.editingGroup.client_id)
        if (!response.error) {
          this.showSnackbarMessage('Group updated successfully', 'success')
          this.showEditDialog = false
          this.loadGroups()
        } else {
          this.showSnackbarMessage(response.message || 'Error updating group', 'error')
        }
      } catch (err) {
        log("Error updating group:", err)
        this.showSnackbarMessage('Error updating group', 'error')
      } finally {
        this.editLoading = false
      }
    },

    confirmDelete(group) {
      this.deletingGroup = group
      this.showDeleteDialog = true
    },

    async deleteGroup() {
      this.deleteLoading = true
      try {
        const response = await deleteImsiGroup(this.token, this.deletingGroup.id)
        if (!response.error) {
          this.showSnackbarMessage('Group deleted successfully', 'success')
          this.showDeleteDialog = false
          this.loadGroups()
        } else {
          this.showSnackbarMessage(response.message || 'Error deleting group', 'error')
        }
      } catch (err) {
        log("Error deleting group:", err)
        this.showSnackbarMessage('Error deleting group', 'error')
      } finally {
        this.deleteLoading = false
      }
    },

    async viewMembers(group) {
      this.selectedGroup = group
      this.membersPage = 1
      this.selectedMembers = []
      this.showMembersDialog = true
      await this.loadGroupMembers()
    },

    async loadGroupMembers() {
      this.loadingMembers = true
      try {
        const response = await getImsiGroupMembers(this.token, this.selectedGroup.id, this.membersPage, 20)
        if (!response.error) {
          this.groupMembers = response.members || []
          this.membersTotalPages = response.total_pages || 1
        } else {
          this.showSnackbarMessage(response.message || 'Error loading members', 'error')
        }
      } catch (err) {
        log("Error loading members:", err)
        this.showSnackbarMessage('Error loading members', 'error')
      } finally {
        this.loadingMembers = false
      }
    },

    async removeSelectedMembers() {
      if (this.selectedMembers.length === 0) return
      this.removeLoading = true
      try {
        const response = await removeImsisFromGroup(this.token, this.selectedGroup.id, [], this.selectedMembers)
        if (!response.error) {
          this.showSnackbarMessage(response.message || 'Removed from group', 'success')
          this.selectedMembers = []
          this.loadGroupMembers()
          this.loadGroups() // Refresh count
        } else {
          this.showSnackbarMessage(response.message || 'Error removing members', 'error')
        }
      } catch (err) {
        log("Error removing members:", err)
        this.showSnackbarMessage('Error removing members', 'error')
      } finally {
        this.removeLoading = false
      }
    },

    showSnackbarMessage(message, color = 'success') {
      this.snackbarMessage = message
      this.snackbarColor = color
      this.showSnackbar = true
    },

    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'suspended': 'warning',
        'not_provisioned': 'info'
      }
      return colors[status] || 'grey'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    }
  },
  created() {
    const { getLoggedIn } = useGetLoggedIn()
    getLoggedIn().then(() => {
      this.token = store.getters.getToken
      this.loadGroups()
    })
  }
})
</script>

<style scoped>
.clickable {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}
.clickable:hover {
  color: #1976d2;
}
</style>
